/**
 * BackLink — the canonical "return" affordance.
 *
 * This codebase has two distinct wayfinding needs that were previously blurred
 * together into "breadcrumbs":
 *
 *   1. WHERE AM I?  — the information architecture. Handled by <Breadcrumb /> /
 *      <DualBreadcrumb /> in the layout top bar. It is derived purely from the URL,
 *      is identical for everyone on a given route, uses ">" separators, and lets you
 *      climb the hierarchy. It never depends on how you got here.
 *
 *   2. HOW DO I GET BACK?  — the return path for a detour or a drill-in. That is
 *      THIS component. It is a single, explicitly-labeled affordance ("← Back to
 *      Cycles") with a back arrow, visually and semantically different from the IA
 *      trail. When the user arrived via a return-to-origin detour, it returns them
 *      to their exact point of entry; otherwise it falls back to the logical parent.
 *
 * Keeping these separate is how leading platforms resolve the tension: a breadcrumb
 * always means "location in the tree," and a separate, clearly-worded "Back to …"
 * link means "the place you came from." Don't reuse a breadcrumb to act as a back
 * button.
 */

import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useOriginNavigation } from '../../lib/navigation/returnToOrigin';

export interface BackLinkProps {
  /** Fallback destination (the logical parent) when there is no navigation origin. */
  to: string;
  /** Name of the fallback destination, e.g. "Cycles" → renders "Back to Cycles". */
  label: string;
  /**
   * When true and the user arrived via a return-to-origin detour, the link returns
   * them to that exact origin instead of the fallback parent. Default true.
   */
  preferOrigin?: boolean;
  className?: string;
}

const BASE_CLASSES =
  'inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] transition-colors';

export function BackLink({ to, label, preferOrigin = true, className }: BackLinkProps) {
  const { hasOrigin, isStale, goBack } = useOriginNavigation();
  const useOrigin = preferOrigin && hasOrigin && !isStale;

  const classes = className ? `${BASE_CLASSES} ${className}` : BASE_CLASSES;

  // Returning to the point of entry: the destination is wherever the user came
  // from, so we keep the label generic and honest rather than naming a parent.
  if (useOrigin) {
    return (
      <button type="button" onClick={() => goBack()} className={classes}>
        <ChevronLeft className="h-4 w-4 flex-shrink-0" />
        <span>Back</span>
      </button>
    );
  }

  // No origin: climb to the logical parent and name it.
  return (
    <Link to={to} className={classes}>
      <ChevronLeft className="h-4 w-4 flex-shrink-0" />
      <span>Back to {label}</span>
    </Link>
  );
}

export default BackLink;
